import { useEffect, useState } from "react";

import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";
import { scopeTenant } from "../lib/tenantScope.mjs";

/**
 * Insights surface. Lists rows from public.insight for the current
 * tenant, sorted by severity (critical, warning, info) then most recent
 * generated_at. The same Claude-generated insights the `meridian
 * insights <slug>` CLI shows, but inside the dashboard so AJ and Seth
 * Heath don't have to drop to the terminal.
 *
 * MER-144. Mirrors AuditFindings (MER-146 audit half) but reads the
 * INSIGHT canonical entity instead of audit_finding.
 */

interface Insight {
  insight_id: string;
  customer_id: string | null;
  generated_at: string | null;
  insight_type: string | null;
  severity: string | null;
  title: string | null;
  body: string | null;
  data_context: string | null;
  model_used: string | null;
  division: string | null;
  equipment_type: string | null;
  period_end_date: string | null;
}

const SEVERITY_RANK: Record<string, number> = {
  CRITICAL: 0,
  WARNING: 1,
  INFO: 2,
};

function severityColor(sev: string): { bg: string; fg: string } {
  switch (sev?.toUpperCase()) {
    case "CRITICAL":
      return { bg: "rgba(217, 75, 75, 0.12)", fg: "var(--flag-red)" };
    case "WARNING":
      return { bg: "rgba(221, 160, 70, 0.15)", fg: "var(--flag-yellow)" };
    case "INFO":
      return { bg: "rgba(82, 119, 178, 0.12)", fg: "rgba(60, 90, 140, 1)" };
    default:
      return { bg: "var(--bg-card)", fg: "var(--text-muted)" };
  }
}

export default function Insights() {
  const [state, setState] = useState<{
    insights: Insight[];
    isLoading: boolean;
    error: string | null;
  }>({ insights: [], isLoading: true, error: null });
  const tenantId = useTenantId();

  useEffect(() => {
    if (!tenantId) return;
    const boundTenantId = tenantId;
    let cancelled = false;
    async function run() {
      const supabase = getSupabase();
      const { data, error } = await scopeTenant(
        supabase
          .from("insight")
          .select(
            "insight_id, customer_id, generated_at, insight_type, severity, title, body, data_context, model_used, division, equipment_type, period_end_date",
          ),
        boundTenantId,
      )
        .order("generated_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (cancelled) return;
      if (error) {
        setState({ insights: [], isLoading: false, error: error.message });
        return;
      }
      const rows = (data ?? []) as Insight[];
      rows.sort((a, b) => {
        const sa = SEVERITY_RANK[a.severity?.toUpperCase() ?? ""] ?? 99;
        const sb = SEVERITY_RANK[b.severity?.toUpperCase() ?? ""] ?? 99;
        if (sa !== sb) return sa - sb;
        const da = a.generated_at ?? "";
        const db = b.generated_at ?? "";
        return db.localeCompare(da);
      });
      setState({ insights: rows, isLoading: false, error: null });
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return (
    <main style={{ padding: "24px 32px" }}>
      <header style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "var(--text-muted)",
          }}
        >
          Internal
        </div>
        <h1 style={{ margin: "4px 0 0 0", fontSize: 22 }}>Insights</h1>
        <p style={{ marginTop: 8, fontSize: 13, color: "var(--text-muted)" }}>
          AI-generated insights from the latest refresh. Sorted by severity, then most recent.
        </p>
      </header>

      {state.isLoading ? (
        <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading...</div>
      ) : state.error ? (
        <div style={{ padding: 20, color: "var(--flag-red)" }}>Error: {state.error}</div>
      ) : state.insights.length === 0 ? (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-muted)",
            background: "var(--bg-card)",
            border: "1px dashed var(--border-subtle)",
            borderRadius: 12,
          }}
        >
          No insights generated for this customer yet. Run `meridian insights {"<slug>"}` to
          generate.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {state.insights.map((it) => {
            const sev = (it.severity ?? "").toUpperCase();
            const color = severityColor(sev);
            const generated = it.generated_at ? it.generated_at.slice(0, 10) : null;
            return (
              <article
                key={it.insight_id}
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 12,
                  padding: "16px 20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      padding: "3px 10px",
                      borderRadius: 999,
                      background: color.bg,
                      color: color.fg,
                      textTransform: "uppercase",
                    }}
                  >
                    {sev || "info"}
                  </span>
                  {it.insight_type ? (
                    <span
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "var(--text-muted)",
                      }}
                    >
                      {it.insight_type}
                    </span>
                  ) : null}
                  {it.division ? (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{it.division}</span>
                  ) : null}
                  {it.equipment_type ? (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {it.equipment_type}
                    </span>
                  ) : null}
                  {generated ? (
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        marginLeft: "auto",
                      }}
                    >
                      {generated}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                  {it.title ?? "(untitled)"}
                </div>
                {it.body ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--text-muted)",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {it.body}
                  </div>
                ) : null}
                {it.model_used ? (
                  <div style={{ fontSize: 11, marginTop: 8, color: "var(--text-muted)" }}>
                    Model: {it.model_used}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}
